import { useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import { einMask, ssnMask } from '@interxfi/design-system/components/input/text-input/masks';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import { type I18nNamespaces, type TFunction, useTranslation } from '@interxfi/translation';
import {
  IXC_REGISTRATION_V1_ORGANIZATION_TAX_IDENTIFIER_ONE_OF_VALUES,
  type IxcRegistrationV1OrganizationTaxIdentifier,
  type IxcRegistrationV1OrganizationTaxIdentifierOneOfValue,
} from '@interxfi/types/generated/api';
import { sortByKey } from '@interxfi/utilities/sort';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { AppPasswordInputWithLens } from '../../../../../components/form/app-password-input';
import { AppSelectInputWithLens } from '../../../../../components/form/app-select-input';
import { EIN_PATTERN, SSN_PATTERN } from '../../../shared-utils/tax-id';

export const ssnFormSchema = z.object({
  type: z.literal('ssn'),
  value: z.string().regex(SSN_PATTERN, { error: 'SSN must be in the format XXX-XX-XXXX' }).min(1, { error: 'SSN is required' }),
});

export const einFormSchema = z.object({
  type: z.literal('ein'),
  value: z.string().regex(EIN_PATTERN, { error: 'EIN must be in the format XX-XXXXXXX' }).min(1, { error: 'EIN is required' }),
});

export const organizationTaxIdFormSchema = z.discriminatedUnion('type', [ssnFormSchema, einFormSchema]);

export type OrganizationTaxIdFormValues = z.infer<typeof organizationTaxIdFormSchema>;

const defaultOrganizationTaxIdFormValuesByType: Record<string, OrganizationTaxIdFormValues> = {
  ssn: {
    type: 'ssn',
    value: '',
  },
  ein: {
    type: 'ein',
    value: '',
  },
} as const;

export const defaultOrganizationTaxIdValues: OrganizationTaxIdFormValues = defaultOrganizationTaxIdFormValuesByType.ein;

export function getInitialFormValuesForOrganizationTaxId(
  taxId: IxcRegistrationV1OrganizationTaxIdentifier | undefined,
): OrganizationTaxIdFormValues | undefined {
  if (!taxId) {
    return undefined;
  }

  if ('ssn' in taxId) {
    return { type: 'ssn', value: taxId.ssn?.number ?? '' };
  }

  if ('ein' in taxId) {
    return { type: 'ein', value: taxId.ein?.number ?? '' };
  }

  exhaustiveCheck(taxId);

  return undefined;
}

function getTaxIdTypeOptions(t: TFunction): InputDropdownOption<IxcRegistrationV1OrganizationTaxIdentifierOneOfValue>[] {
  return sortByKey(
    IXC_REGISTRATION_V1_ORGANIZATION_TAX_IDENTIFIER_ONE_OF_VALUES.map((item) => ({
      label: t(`ixcRegistrationV1:oneOf.IxcRegistrationV1OrganizationTaxIdentifier.${item}`),
      value: item,
    })),
    (x) => x.label,
  );
}

export function formValuesToOrganizationTaxIdentifier(taxIdentifier: OrganizationTaxIdFormValues): IxcRegistrationV1OrganizationTaxIdentifier {
  return match(taxIdentifier)
    .returnType<IxcRegistrationV1OrganizationTaxIdentifier>()
    .with({ type: 'ein' }, ({ value }) => ({ ein: { number: value } }))
    .with({ type: 'ssn' }, ({ value }) => ({ ssn: { number: value } }))
    .exhaustive();
}

const DEFAULT_ORGANIZATION_TAX_ID_COLUMNS: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
};

interface OrganizationTaxIdFormProps {
  columns?: ResponsiveValue<number>;
  lens: Lens<OrganizationTaxIdFormValues>;
  withoutContainer?: boolean;
}

const i18nNamespaces: I18nNamespaces[] = ['ixcRegistrationV1'] as const;

export function OrganizationTaxIdForm({ columns = DEFAULT_ORGANIZATION_TAX_ID_COLUMNS, lens, withoutContainer }: OrganizationTaxIdFormProps) {
  const { t } = useTranslation(i18nNamespaces);
  const { setValue } = useFormContext();
  const { name } = lens.interop();
  const typeLensInterop = lens.focus('type').interop();
  const typeValue = useWatch({ ...typeLensInterop, defaultValue: 'ssn' });
  const mask = typeValue === 'ein' ? einMask : ssnMask;
  const typeOptions = useMemo(() => getTaxIdTypeOptions(t), [t]);

  const inputElements = (
    <>
      <AppSelectInputWithLens
        label="Tax Identifier"
        lens={lens.focus('type')}
        onChange={(e) => {
          setValue(
            name,
            { ...defaultOrganizationTaxIdFormValuesByType[e.target.value as IxcRegistrationV1OrganizationTaxIdentifierOneOfValue] },
            {
              shouldValidate: true,
            },
          );
        }}
        options={typeOptions}
        required
      />

      <AppPasswordInputWithLens
        autoComplete="off"
        key="taxIdValueInput"
        label={t(`ixcRegistrationV1:oneOf.IxcRegistrationV1OrganizationTaxIdentifier.${typeValue}`)}
        lens={lens.focus('value')}
        maskOptions={mask}
        required
      />
    </>
  );

  if (withoutContainer) {
    return inputElements;
  }

  return (
    <Grid autoColumns={columns} gap={4}>
      {inputElements}
    </Grid>
  );
}
